import { Status } from "../../components/icons/status";
import { Button } from "../../components/ui/button/button";
import { cn } from "../../lib/utils";
import { Task, TaskTypes, useTasks } from "../../queries/tasks/use-tasks";
import { EngraveTask } from "./types/engrave-task";
import { ComponentType, useCallback } from "react";
import { Skeleton } from "../../components/ui/skeleton/skeleton";
import React from "react";
import { PackagingPrinting } from "./types/packaging-printing-task";
import { QueryError } from "@/components/ui/query-error/query-error";
import { useTranslation } from "react-i18next";
import { MarkingTask } from "./types/marking-task";
import { Card } from "../../components/ui/card/card";
import { Barcode } from "lucide-react";
import { useMTUs } from "../../queries/mtu/use-mtus";

/**
 * Add more task type here by adding a component handler for each task key.
 */
const taskComponentMap: Record<TaskTypes, ComponentType<{ task?: Task }>> = {
  GF_EngraverTaskDefinition: EngraveTask,
  GF_PackingTaskDefinition: PackagingPrinting,
  GF_MarkingTaskDefinition: MarkingTask,
};

export function Tasks() {
  const { t } = useTranslation();
  const { selected } = useMTUs();
  const {
    incompleteTasks,
    selectedTask,
    setSelectedTask,
    isPending,
    error,
    refetch,
  } = useTasks();

  const TaskComponent =
    (selectedTask && taskComponentMap[selectedTask.TaskDefinitionNId]) ??
    (() => <React.Fragment />);

  if (error) {
    return (
      <QueryError
        resource={t("task.tasks")}
        errors={[error]}
        retries={[refetch]}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-1.5 flex-wrap">
        {incompleteTasks?.length === 0 && (
          <Card
            className={cn("p-4 flex-grow shadow-task-sm bg-success-lighter")}
          >
            <p className="flex gap-2 text-xl font-bold items-center">
              <Barcode className="h-6 w-6" />
              {selected?.DM_MaterialTrackingUnit.MaterialTrackingUnit.Code}
            </p>

            <p>{t("task.allCompleted")}</p>
          </Card>
        )}
        {isPending &&
          [...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-24 flex-1" />
          ))}
        {incompleteTasks?.map((task) => (
          <TaskButton
            className={
              incompleteTasks.length > 3
                ? "basis-[calc(33.3333%-4px)]"
                : "flex-1"
            }
            key={task.Id}
            selected={selectedTask?.Id === task.Id}
            onSelect={setSelectedTask}
            {...task}
          />
        ))}
      </div>

      {isPending && (
        <>
          <Skeleton className="h-[90px] w-full" />
          <Skeleton className="h-[40px] w-full" />
        </>
      )}

      <TaskComponent task={selectedTask} />
    </div>
  );
}

function TaskButton({
  selected,
  onSelect,
  className,
  ...task
}: Task & {
  className?: string;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation();
  const status = task.Status?.StatusNId;

  const taskTitle: Record<TaskTypes, string> = {
    GF_EngraverTaskDefinition: t("task.engrave.title"),
    GF_PackingTaskDefinition: t("task.packagingPrinting.title"),
    GF_MarkingTaskDefinition: t("task.marking.title"),
  };

  const handleClick = useCallback(() => {
    onSelect(task.Id!);
  }, [onSelect, task.Id]);

  return (
    <Button
      variant="outline"
      size="default"
      className={cn(
        selected && "bg-primary border-primary text-primary-foreground",
        "h-12 font-semibold gap-2 pl-2.5 shadow-task-md ring-0 border",
        className
      )}
      onClick={handleClick}
    >
      {status === "InProgress" && <Status className="h-4 w-4" />}
      {taskTitle[task.TaskDefinitionNId]}
    </Button>
  );
}
